import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { Plus, Pencil, Trash2, Check, X, Eye, EyeOff, Columns3, Lock, GripVertical } from "lucide-react";
import {
  listarColunasKanban,
  criarColunaKanban,
  atualizarColunaKanban,
  excluirColunaKanban,
  reordenarColunasKanban,
  ColunaKanban,
} from "../services/colunasKanban";
import { useConfirm } from "../components/ConfirmProvider";
import { useAuth } from "../hooks/useAuth";

export function ColunasKanbanAdmin() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { can } = useAuth();
  const ehAdmin = can(["admin"]);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);

  const { data: colunasData = [], isLoading } = useQuery({
    queryKey: ["colunas-kanban", "todos"],
    queryFn: () => listarColunasKanban({ todos: true }),
  });
  // Cópia local pra reordenação otimista por drag.
  const [lista, setLista] = useState<ColunaKanban[]>([]);
  useEffect(() => { setLista(colunasData); }, [colunasData]);

  const criarM = useMutation({
    mutationFn: criarColunaKanban,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["colunas-kanban"] }); setNovo(false); },
  });
  const atualizarM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => atualizarColunaKanban(id, dados),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["colunas-kanban"] }); setEditandoId(null); },
  });
  const excluirM = useMutation({
    mutationFn: excluirColunaKanban,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["colunas-kanban"] }),
    onError: (e: any) => confirm({ title: "Não foi possível excluir", message: e?.response?.data?.error?.message || "Erro", confirmText: "OK" }),
  });
  const reordenarM = useMutation({
    mutationFn: reordenarColunasKanban,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["colunas-kanban"] }),
  });

  async function aoExcluir(c: ColunaKanban) {
    const ok = await confirm({
      title: `Excluir "${c.label}"?`,
      message: "Se houver tarefas nesta coluna, a exclusão será bloqueada — mova-as ou desative a coluna.",
      danger: true,
      confirmText: "Excluir",
    });
    if (ok) excluirM.mutate(c.id);
  }

  function onDragEnd(result: DropResult) {
    if (!result.destination) return;
    const de = result.source.index;
    const para = result.destination.index;
    if (de === para) return;
    const nova = Array.from(lista);
    const [movido] = nova.splice(de, 1);
    nova.splice(para, 0, movido);
    setLista(nova); // otimista
    reordenarM.mutate(nova.map((c, i) => ({ id: c.id, ordem: i })));
  }

  if (!can(["gestor", "admin"])) return <div className="text-slate-500">Acesso restrito.</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Columns3 size={20} /> Colunas do quadro
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Colunas do quadro de tarefas (kanban). <strong>Arraste pela alça</strong> para reordenar, renomeie, recoloria, adicione ou remova. As colunas de <strong>aprovação</strong> e <strong>finalização</strong> são protegidas (não podem ser excluídas).
          </p>
        </div>
        <button onClick={() => setNovo(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <Plus size={16} /> Nova coluna
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-3 space-y-2">
        {novo && (
          <LinhaEditavel coluna={null} onSalvar={(d) => criarM.mutate(d)} onCancelar={() => setNovo(false)} salvando={criarM.isPending} />
        )}
        {isLoading && <p className="px-2 py-6 text-center text-slate-400 text-sm">Carregando…</p>}
        {!isLoading && lista.length === 0 && !novo && <p className="px-2 py-6 text-center text-slate-400 text-sm">Nenhuma coluna.</p>}

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="colunas">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-1.5">
                {lista.map((c, index) => (
                  <Draggable key={c.id} draggableId={c.id} index={index} isDragDisabled={editandoId === c.id}>
                    {(prov, snap) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        className={`rounded-lg border border-slate-200 dark:border-slate-700 ${snap.isDragging ? "shadow-lg bg-white dark:bg-slate-800" : "bg-slate-50 dark:bg-slate-800/40"}`}
                      >
                        {editandoId === c.id ? (
                          <LinhaEditavel coluna={c} onSalvar={(d) => atualizarM.mutate({ id: c.id, dados: d })} onCancelar={() => setEditandoId(null)} salvando={atualizarM.isPending} />
                        ) : (
                          <div className="flex items-center gap-2 px-2 py-2">
                            <span {...prov.dragHandleProps} className="cursor-grab text-slate-400 hover:text-slate-600 px-1" title="Arraste para reordenar">
                              <GripVertical size={16} />
                            </span>
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium" style={{ background: c.cor, color: "#fff" }}>
                              {c.emoji && <span>{c.emoji}</span>}
                              <span>{c.label}</span>
                            </span>
                            <span className="font-mono text-[11px] text-slate-400">{c.chave}</span>
                            {(c.ehFinal || c.ehAprovacao) && (
                              <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5"><Lock size={10} /> {c.ehFinal ? "finalização" : "aprovação"}</span>
                            )}
                            {!c.ativo && <span className="text-[10px] uppercase font-bold text-slate-500 bg-slate-200 dark:bg-slate-700 px-1.5 py-0.5 rounded">Inativa</span>}
                            <div className="ml-auto flex items-center">
                              <button onClick={() => atualizarM.mutate({ id: c.id, dados: { ativo: !c.ativo } })} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title={c.ativo ? "Desativar" : "Ativar"}>
                                {c.ativo ? <EyeOff size={14} /> : <Eye size={14} />}
                              </button>
                              <button onClick={() => setEditandoId(c.id)} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Editar">
                                <Pencil size={14} />
                              </button>
                              {ehAdmin && !c.ehFinal && !c.ehAprovacao && (
                                <button onClick={() => aoExcluir(c)} className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded" title="Excluir">
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
}

function LinhaEditavel({ coluna, onSalvar, onCancelar, salvando }: {
  coluna: ColunaKanban | null;
  onSalvar: (dados: { label: string; cor: string; emoji: string }) => void;
  onCancelar: () => void;
  salvando: boolean;
}) {
  const [label, setLabel] = useState(coluna?.label || "");
  const [cor, setCor] = useState(coluna?.cor || "#94a3b8");
  const [emoji, setEmoji] = useState(coluna?.emoji || "");

  function salvar() {
    if (!label.trim()) return;
    onSalvar({ label: label.trim(), cor, emoji });
  }

  return (
    <div className="flex items-center gap-1.5 px-2 py-2 bg-blue-50/40 dark:bg-blue-900/20 rounded-lg">
      <input type="color" value={cor} onChange={(e) => setCor(e.target.value)} className="w-8 h-7 rounded cursor-pointer border border-slate-300" title="Cor" />
      <input type="text" value={emoji} onChange={(e) => setEmoji(e.target.value)} placeholder="(emoji)" maxLength={4} className="w-12 h-7 rounded text-center border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-base" title="Emoji opcional" />
      <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex.: Ajustes" className="flex-1 min-w-[160px] px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm outline-none focus:border-blue-500" autoFocus />
      <span className="font-mono text-[11px] text-slate-400">{coluna ? coluna.chave : "(auto)"}</span>
      <button onClick={salvar} disabled={!label.trim() || salvando} className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded disabled:opacity-40" title="Salvar"><Check size={14} /></button>
      <button onClick={onCancelar} className="p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" title="Cancelar"><X size={14} /></button>
    </div>
  );
}
